import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Bot,
  Settings,
  MessageSquare,
  Zap,
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  HelpCircle,
  TrendingUp,
  Users,
  Search,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Sidebar from "@/components/Sidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_COLORS = {
  NEW: "bg-blue-100 text-blue-700",
  AWAITING_ORDER_ID: "bg-amber-100 text-amber-700",
  AWAITING_PHOTOS: "bg-purple-100 text-purple-700",
  COMPLETE: "bg-green-100 text-green-700",
  ESCALATED: "bg-red-100 text-red-700",
};

const FAQ_CATEGORIES = [
  { value: "general", label: "General" },
  { value: "delivery", label: "Delivery" },
  { value: "payment", label: "Payment" },
  { value: "quality", label: "Quality" },
  { value: "order", label: "Order" },
  { value: "from_queries", label: "From Queries" },
];

export default function AIAgentPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("config");
  const [loading, setLoading] = useState(true);
  
  // Config state
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  
  // FAQ state
  const [faqs, setFaqs] = useState([]);
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({
    question: "",
    answer: "",
    keywords: "",
    category: "general",
    auto_escalate: false,
  });
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  // Unmatched queries state
  const [unmatchedQueries, setUnmatchedQueries] = useState([]);
  const [answerDialogOpen, setAnswerDialogOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [answerForm, setAnswerForm] = useState({ answer: "", keywords: "" });

  useEffect(() => {
    fetchConfig();
    fetchFaqs();
    fetchAnalytics();
    fetchUnmatchedQueries();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/ai-agent/config`);
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching config:", error);
      toast.error("Failed to load AI Agent config");
    } finally {
      setLoading(false);
    }
  };

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/ai-agent/faqs?include_inactive=true`);
      setFaqs(response.data);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/ai-agent/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  const fetchUnmatchedQueries = async () => {
    try {
      const response = await axios.get(`${API}/ai-agent/unmatched-queries`);
      setUnmatchedQueries(response.data);
    } catch (error) {
      console.error("Error fetching unmatched queries:", error);
    }
  };

  const updateConfig = async (updates) => {
    setConfigLoading(true);
    try {
      await axios.put(`${API}/ai-agent/config`, updates);
      setConfig((prev) => ({ ...prev, ...updates }));
      toast.success("Configuration updated");
    } catch (error) {
      console.error("Error updating config:", error);
      toast.error("Failed to update configuration");
    } finally {
      setConfigLoading(false);
    }
  };

  const handleToggleEnabled = async () => {
    await updateConfig({ enabled: !config?.enabled });
  };

  const saveFaq = async () => {
    try {
      const data = {
        ...faqForm,
        keywords: faqForm.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      };

      if (editingFaq) {
        await axios.put(`${API}/ai-agent/faqs/${editingFaq.id}`, data);
        toast.success("FAQ updated");
      } else {
        await axios.post(`${API}/ai-agent/faqs`, data);
        toast.success("FAQ created");
      }

      setFaqDialogOpen(false);
      setEditingFaq(null);
      setFaqForm({ question: "", answer: "", keywords: "", category: "general", auto_escalate: false });
      fetchFaqs();
    } catch (error) {
      console.error("Error saving FAQ:", error);
      toast.error("Failed to save FAQ");
    }
  };

  const deleteFaq = async (id) => {
    if (!confirm("Delete this FAQ?")) return;
    try {
      await axios.delete(`${API}/ai-agent/faqs/${id}`);
      toast.success("FAQ deleted");
      fetchFaqs();
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.error("Failed to delete FAQ");
    }
  };

  const openEditFaq = (faq) => {
    setEditingFaq(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      keywords: faq.keywords?.join(", ") || "",
      category: faq.category || "general",
      auto_escalate: faq.auto_escalate || false,
    });
    setFaqDialogOpen(true);
  };

  const createFaqFromQuery = async () => {
    if (!selectedQuery || !answerForm.answer) return;
    try {
      await axios.post(
        `${API}/ai-agent/unmatched-queries/${selectedQuery.id}/create-faq`,
        null,
        {
          params: {
            answer: answerForm.answer,
            keywords: answerForm.keywords.split(",").map((k) => k.trim()).filter(Boolean),
          },
        }
      );
      toast.success("FAQ created from query");
      setAnswerDialogOpen(false);
      setSelectedQuery(null);
      setAnswerForm({ answer: "", keywords: "" });
      fetchFaqs();
      fetchUnmatchedQueries();
    } catch (error) {
      console.error("Error creating FAQ:", error);
      toast.error("Failed to create FAQ");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-muted/30">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 pb-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Bot className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">AI Agent</h1>
              <p className="text-muted-foreground">Thunder Buddy WhatsApp Assistant</p>
            </div>
          </div>

          {/* Master Toggle */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${config?.enabled ? "text-green-600" : "text-muted-foreground"}`}>
                {config?.enabled ? "Agent Active" : "Agent Disabled"}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleEnabled}
                disabled={configLoading}
                className={config?.enabled ? "text-green-600" : "text-muted-foreground"}
              >
                {config?.enabled ? (
                  <ToggleRight className="h-8 w-8" />
                ) : (
                  <ToggleLeft className="h-8 w-8" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="config" className="gap-2">
                <Settings className="h-4 w-4" />
                Configuration
              </TabsTrigger>
              <TabsTrigger value="faqs" className="gap-2">
                <HelpCircle className="h-4 w-4" />
                FAQs ({faqs.length})
              </TabsTrigger>
              <TabsTrigger value="queries" className="gap-2">
                <MessageSquare className="h-4 w-4" />
                Unmatched ({unmatchedQueries.length})
              </TabsTrigger>
              <TabsTrigger value="analytics" className="gap-2">
                <TrendingUp className="h-4 w-4" />
                Analytics
              </TabsTrigger>
            </TabsList>

            {/* Configuration Tab */}
          <TabsContent value="config">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Bot Settings</CardTitle>
                  <CardDescription>Configure the AI agent behavior</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Bot Name</Label>
                      <Input
                        value={config?.bot_name || ""}
                        onChange={(e) => setConfig((prev) => ({ ...prev, bot_name: e.target.value }))}
                        onBlur={() => updateConfig({ bot_name: config?.bot_name })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Bot Gender</Label>
                      <Select
                        value={config?.bot_gender || "female"}
                        onValueChange={(value) => updateConfig({ bot_gender: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="female">👩 Female (speaks like a helpful sister)</SelectItem>
                          <SelectItem value="male">👨 Male (speaks like a helpful brother)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Bot will use gender-appropriate language style
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Greeting Message</Label>
                    <Textarea
                      value={config?.greeting_message || ""}
                      onChange={(e) => setConfig((prev) => ({ ...prev, greeting_message: e.target.value }))}
                      onBlur={() => updateConfig({ greeting_message: config?.greeting_message })}
                      rows={3}
                    />
                  </div>
                  
                  <Separator />
                  
                  {/* Reply Delay */}
                  <div className="space-y-2">
                    <Label>Reply Delay (seconds)</Label>
                    <p className="text-xs text-muted-foreground">
                      Wait time before bot responds (makes it feel more natural)
                    </p>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="10"
                      value={config?.reply_delay_seconds || 1.5}
                      onChange={(e) => setConfig((prev) => ({ ...prev, reply_delay_seconds: parseFloat(e.target.value) }))}
                      onBlur={() => updateConfig({ reply_delay_seconds: config?.reply_delay_seconds })}
                    />
                  </div>
                  
                  {/* Supported Languages */}
                  <div className="space-y-2">
                    <Label>Supported Languages</Label>
                    <p className="text-xs text-muted-foreground">
                      Bot will respond in the language customer uses
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {["Hindi", "English", "Hinglish", "Tamil", "Telugu"].map((lang) => (
                        <Badge
                          key={lang}
                          variant={config?.supported_languages?.includes(lang) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => {
                            const current = config?.supported_languages || [];
                            const updated = current.includes(lang)
                              ? current.filter((l) => l !== lang)
                              : [...current, lang];
                            updateConfig({ supported_languages: updated });
                          }}
                        >
                          {lang}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* API Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>API Configuration</CardTitle>
                  <CardDescription>Gemini API settings for AI responses</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Use Custom API Key</Label>
                      <p className="text-xs text-muted-foreground">
                        {config?.use_custom_key ? "Using your custom Gemini key" : "Using Emergent Universal Key"}
                      </p>
                    </div>
                    <Switch
                      checked={config?.use_custom_key || false}
                      onCheckedChange={(checked) => updateConfig({ use_custom_key: checked })}
                    />
                  </div>
                  
                  {config?.use_custom_key && (
                    <div className="space-y-2">
                      <Label>Custom Gemini API Key</Label>
                      <Input
                        type="password"
                        placeholder="Enter your Gemini API key"
                        value={config?.custom_api_key || ""}
                        onChange={(e) => setConfig((prev) => ({ ...prev, custom_api_key: e.target.value }))}
                        onBlur={() => updateConfig({ custom_api_key: config?.custom_api_key })}
                      />
                      {config?.custom_api_key_masked && (
                        <p className="text-xs text-muted-foreground">Current: {config.custom_api_key_masked}</p>
                      )}
                    </div>
                  )}

                  <Separator />

                  <div className="space-y-2">
                    <Label>Model Provider</Label>
                    <Select
                      value={config?.model_provider || "gemini"}
                      onValueChange={(value) => updateConfig({ model_provider: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gemini">Google Gemini</SelectItem>
                        <SelectItem value="openai">OpenAI</SelectItem>
                        <SelectItem value="anthropic">Anthropic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Model Name</Label>
                    <Input
                      value={config?.model_name || ""}
                      onChange={(e) => setConfig((prev) => ({ ...prev, model_name: e.target.value }))}
                      onBlur={() => updateConfig({ model_name: config?.model_name })}
                      placeholder="gemini-2.0-flash"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Business Training - Full Width */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-500" />
                    Train Your AI Bot
                  </CardTitle>
                  <CardDescription>
                    Add custom business context, rules, and information that the AI will use when responding to customers
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Business Context & Rules</Label>
                    <Textarea
                      placeholder={`Add your business-specific information here. For example:

## About Thunder Buddy
- We are a polaroid photo printing service
- We accept orders through Meesho
- Minimum order: 10 photos
- Maximum photos per order: 50

## Pricing
- Standard polaroid: ₹15/photo
- Premium glossy: ₹25/photo
- Bulk discount: 20% off on 30+ photos

## Delivery Information
- Delivery time: 5-7 days after order verification
- Free shipping on orders above ₹500
- Cash on Delivery available

## Working Hours
- Monday to Saturday: 10 AM - 7 PM
- Sunday: Closed

## Special Instructions
- Ask customers to send high-quality photos
- Minimum resolution: 800x800 pixels
- Supported formats: JPG, PNG
- No blurry or low-light photos accepted`}
                      value={config?.business_context || ""}
                      onChange={(e) => setConfig((prev) => ({ ...prev, business_context: e.target.value }))}
                      onBlur={() => updateConfig({ business_context: config?.business_context })}
                      rows={12}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      💡 Tip: Include pricing, delivery info, working hours, product details, and any special rules
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Custom Goals - Full Width */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    🎯 Custom Goals
                  </CardTitle>
                  <CardDescription>
                    Define numbered goals that the AI bot will actively try to achieve in every conversation
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>AI Bot Goals (Numbered List)</Label>
                    <Textarea
                      placeholder={`Define your bot's goals in numbered format. The AI will prioritize these in order:

1. Collect Meesho Order ID from every customer
2. Ensure customer sends all photos for printing
3. Confirm order details before ending conversation
4. Upsell premium glossy prints when appropriate
5. Ask for referrals if customer is satisfied
6. Collect customer feedback after order completion
7. Promote current offers and discounts
8. Build rapport and ensure customer satisfaction`}
                      value={config?.custom_goals || ""}
                      onChange={(e) => setConfig((prev) => ({ ...prev, custom_goals: e.target.value }))}
                      onBlur={() => updateConfig({ custom_goals: config?.custom_goals })}
                      rows={8}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      🎯 The AI will actively work towards achieving these goals in priority order
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Conversation States Info */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Conversation Flow</CardTitle>
                  <CardDescription>How the AI agent handles customer conversations</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 items-center">
                    <Badge className="bg-blue-100 text-blue-700">NEW</Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <Badge className="bg-amber-100 text-amber-700">AWAITING_ORDER_ID</Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <Badge className="bg-purple-100 text-purple-700">AWAITING_PHOTOS</Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <Badge className="bg-green-100 text-green-700">COMPLETE</Badge>
                  </div>
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800 font-medium">
                      📋 Order Status = CHECK
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      When bot collects Order ID, it automatically sets order_id_status to "CHECK" in database. 
                      Your team can then verify the order on Meesho.
                    </p>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    Customers can be escalated to humans at any point by saying "help", "talk to human", etc.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* FAQs Tab */}
          <TabsContent value="faqs">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>FAQ Knowledge Base</CardTitle>
                  <CardDescription>Train the bot with common questions and answers</CardDescription>
                </div>
                <Button onClick={() => { setEditingFaq(null); setFaqForm({ question: "", answer: "", keywords: "", category: "general", auto_escalate: false }); setFaqDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add FAQ
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {faqs.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No FAQs yet. Add some to train the bot!
                      </p>
                    ) : (
                      faqs.map((faq) => (
                        <div
                          key={faq.id}
                          className={`p-4 rounded-lg border ${faq.is_active ? "bg-background" : "bg-muted/50 opacity-60"}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-xs">
                                  {faq.category}
                                </Badge>
                                {faq.auto_escalate && (
                                  <Badge className="bg-red-100 text-red-700 text-xs">
                                    Auto-Escalate
                                  </Badge>
                                )}
                                {faq.hit_count > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {faq.hit_count} hits
                                  </Badge>
                                )}
                              </div>
                              <h4 className="font-medium">{faq.question}</h4>
                              <p className="text-sm text-muted-foreground mt-1">{faq.answer}</p>
                              {faq.keywords?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {faq.keywords.map((kw, i) => (
                                    <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded">
                                      {kw}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditFaq(faq)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => deleteFaq(faq.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Unmatched Queries Tab */}
          <TabsContent value="queries">
            <Card>
              <CardHeader>
                <CardTitle>Unmatched Customer Queries</CardTitle>
                <CardDescription>Questions the bot couldn't answer - create FAQs from these</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {unmatchedQueries.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No unmatched queries! The bot is handling everything well.
                      </p>
                    ) : (
                      unmatchedQueries.map((query) => (
                        <div key={query.id} className="p-4 rounded-lg border bg-background">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <p className="text-xs text-muted-foreground mb-1">
                                {query.phone_number} • {new Date(query.created_at).toLocaleString()}
                              </p>
                              <p className="font-medium">"{query.query}"</p>
                              {query.ai_response && (
                                <p className="text-sm text-muted-foreground mt-2 italic">
                                  Bot replied: {query.ai_response}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedQuery(query);
                                setAnswerForm({ answer: "", keywords: query.query.split(" ").slice(0, 3).join(", ") });
                                setAnswerDialogOpen(true);
                              }}
                            >
                              Create FAQ
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Conversations</p>
                      <p className="text-3xl font-bold">{analytics?.total_conversations || 0}</p>
                    </div>
                    <MessageSquare className="h-8 w-8 text-primary/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Escalated</p>
                      <p className="text-3xl font-bold text-red-600">{analytics?.escalated_conversations || 0}</p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-red-200" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Escalation Rate</p>
                      <p className="text-3xl font-bold">{analytics?.escalation_rate || 0}%</p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-primary/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Active FAQs</p>
                      <p className="text-3xl font-bold">{analytics?.total_faqs || 0}</p>
                    </div>
                    <HelpCircle className="h-8 w-8 text-primary/20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* State Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Conversation States</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(analytics?.state_distribution || {}).map(([state, count]) => (
                      <div key={state} className="flex items-center justify-between">
                        <Badge className={STATE_COLORS[state] || "bg-gray-100"}>
                          {state}
                        </Badge>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top FAQs */}
              <Card>
                <CardHeader>
                  <CardTitle>Most Used FAQs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics?.top_faqs?.length > 0 ? (
                      analytics.top_faqs.map((faq, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-sm truncate flex-1 mr-4">{faq.question}</span>
                          <Badge variant="secondary">{faq.hit_count} hits</Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-sm">No FAQ usage data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Unmatched Queries Section */}
            <Card className="mt-6">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Unmatched Queries
                    </CardTitle>
                    <CardDescription>Questions the bot couldn't answer - use these to train new FAQs</CardDescription>
                  </div>
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    {unmatchedQueries.length} pending
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {unmatchedQueries.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {unmatchedQueries.map((query) => (
                      <div 
                        key={query.id} 
                        className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
                        data-testid={`unmatched-query-${query.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 break-words">
                              "{query.query}"
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              From: {query.phone_number || "Unknown"} • {new Date(query.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0 border-amber-400 text-amber-700 hover:bg-amber-100"
                            onClick={() => {
                              setSelectedQuery(query);
                              setAnswerForm({ answer: "", keywords: "" });
                              setAnswerDialogOpen(true);
                            }}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Create FAQ
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500/50" />
                    <p className="font-medium">All caught up!</p>
                    <p className="text-sm">No unmatched queries to review</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* FAQ Dialog */}
        <Dialog open={faqDialogOpen} onOpenChange={setFaqDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingFaq ? "Edit FAQ" : "Add New FAQ"}</DialogTitle>
              <DialogDescription>Train the bot with question-answer pairs</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Question</Label>
                <Input
                  placeholder="What is the delivery time?"
                  value={faqForm.question}
                  onChange={(e) => setFaqForm((prev) => ({ ...prev, question: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Answer</Label>
                <Textarea
                  placeholder="Order verify hone ke baad 5-7 din mein delivery ho jaegi 📦"
                  value={faqForm.answer}
                  onChange={(e) => setFaqForm((prev) => ({ ...prev, answer: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Keywords (comma-separated)</Label>
                <Input
                  placeholder="delivery, time, kitne din, kab aayega"
                  value={faqForm.keywords}
                  onChange={(e) => setFaqForm((prev) => ({ ...prev, keywords: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">Bot will match these keywords in customer messages</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={faqForm.category}
                    onValueChange={(value) => setFaqForm((prev) => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FAQ_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Auto-Escalate</Label>
                  <div className="flex items-center gap-2 h-10">
                    <Switch
                      checked={faqForm.auto_escalate}
                      onCheckedChange={(checked) => setFaqForm((prev) => ({ ...prev, auto_escalate: checked }))}
                    />
                    <span className="text-sm text-muted-foreground">
                      {faqForm.auto_escalate ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFaqDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={saveFaq}>
                {editingFaq ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Answer Query Dialog */}
        <Dialog open={answerDialogOpen} onOpenChange={setAnswerDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create FAQ from Query</DialogTitle>
              <DialogDescription>
                Customer asked: "{selectedQuery?.query}"
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Answer</Label>
                <Textarea
                  placeholder="Enter the answer to this question..."
                  value={answerForm.answer}
                  onChange={(e) => setAnswerForm((prev) => ({ ...prev, answer: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Keywords (comma-separated)</Label>
                <Input
                  placeholder="keyword1, keyword2"
                  value={answerForm.keywords}
                  onChange={(e) => setAnswerForm((prev) => ({ ...prev, keywords: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAnswerDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={createFaqFromQuery}>
                Create FAQ
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
    </div>
  );
}
